import React, { SyntheticEvent, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Icon, type IconName } from 'components/ui/icon';
import { cn } from 'lib/utils';

import Table, { ColumnConfig } from 'components/table';
import ToastContainer, { ToastType } from 'components/toasts/container';
import PaymentStatus from 'views/settings/config/tabs/components/modals/paymentStatus';
import PaymentAdjustment from 'components/modals/paymentAdjustment';
import Pagination from 'components/pagination';

import usePayment from 'state/payment/hooks/usePayment';
import useOrganisation from 'state/organisation/hooks/useOrganisation';
import usePaymentConfig from 'state/paymentConfig/hooks/usePaymentConfig';
import { PAYMENTS } from 'state/payment/type';
import { GTW_STATUS } from 'utils/constants';
import CURRENCY from 'utils/currency';
import { formatToTz } from 'utils/date-format';
import STRING from 'utils/string';
import { apiWithOrg } from 'services/api';
import {
  gatewatTransactionStatusFormatter,
  transactionStatusFormatter,
} from 'utils/status-formatter';

import { Theme } from '@table-library/react-table-library/types/theme';

const INVOICE_STATUS = {
  NEW: 'new',
  CREATED: 'created',
  SUBMITTED: 'submitted',
  CAPTURED: 'captured',
  CANCELED: 'canceled',
  CANCELLED: 'cancelled',
  ERROR: 'error',
  FAILED: 'failed',
  VOIDED: 'voided',
} as const;

const PAYMENT_METHOD = {
  ACH: 'ACH',
  ATM: 'ATM',
  CATM: 'CATM',
} as const;

const INVOICE_BADGE_MAP: Record<
  string,
  {
    variant:
      | 'default'
      | 'secondary'
      | 'destructive'
      | 'outline'
      | 'info'
      | 'success'
      | 'progress'
      | 'disabled'
      | 'refund';
    label: string;
    className?: string;
  }
> = {
  // Early, pre-capture states use the soft `info` (blue) pill, matching
  // "Initiated"; green stays reserved for success/captured.
  [INVOICE_STATUS.NEW]: { variant: 'info', label: 'Created' },
  [INVOICE_STATUS.CREATED]: { variant: 'info', label: 'Created' },
  [INVOICE_STATUS.SUBMITTED]: { variant: 'info', label: 'Submitted' },
  [INVOICE_STATUS.CAPTURED]: {
    variant: 'default',
    label: 'Captured',
    className: 'bg-green-500 text-white',
  },
  [INVOICE_STATUS.CANCELED]: { variant: 'secondary', label: 'Canceled' },
  [INVOICE_STATUS.CANCELLED]: { variant: 'secondary', label: 'Canceled' },
  [INVOICE_STATUS.ERROR]: { variant: 'destructive', label: 'Error' },
  [INVOICE_STATUS.FAILED]: { variant: 'destructive', label: 'Error' },
  [INVOICE_STATUS.VOIDED]: { variant: 'secondary', label: 'Voided' },
};

const invoiceStatusFormatter = (status: string): JSX.Element => {
  const key = status?.toLowerCase();
  const config = INVOICE_BADGE_MAP[key];
  if (!config) {
    return <Badge variant="default">{status || 'Unknown'}</Badge>;
  }
  return (
    <Badge variant={config.variant} className={config.className}>
      {config.label}
    </Badge>
  );
};

export interface ITableData extends PAYMENTS.ITransactionResult {
  customerName: string;
  grossAmount: number;
  transactionDate: string;
}

interface IProps {
  isActiveTab: boolean;
}

interface ActionMenuItem {
  testId: string;
  icon: IconName;
  title: string;
  description: string;
  disabled?: boolean;
  onClick: () => void;
}

const ActionMenuItemRow = ({ item }: { item: ActionMenuItem }) => (
  <DropdownMenuItem
    data-testid={item.testId}
    disabled={item.disabled}
    onClick={item.onClick}
    className={cn(
      'flex-col items-start gap-1 cursor-pointer p-2',
      item.disabled && 'cursor-not-allowed opacity-50'
    )}
  >
    <span className="flex items-center gap-2 text-sm text-gray-blue-80">
      <Icon
        name={item.icon}
        size={16}
        className="text-muted-foreground"
        aria-hidden="true"
      />
      {item.title}
    </span>
    <span className="text-xs text-gray-blue-60 whitespace-normal ml-6">
      {item.description}
    </span>
  </DropdownMenuItem>
);

const RecentPayments = ({ isActiveTab }: IProps): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId } = useParams<{ orgId: string }>();

  const [adjustmentToast, setAdjustmentToast] = useState(false);
  const [showPaymentStatusModal, setShowPaymentStatusModal] = useState(false);
  const [recordSale, setRecordSale] = useState(false);
  const [recordSaleData, setRecordSaleData] = useState<any>();
  const [refreshTnx, setRefreshTnx] = useState(false);
  const [prevKey, setPrevKey] = useState<string | null>(null);
  const [nextKey, setNextKey] = useState<string | null>(null);
  const [numResults, setNumResults] = useState(0);

  const [toast, setToast] = useState<{
    show: boolean;
    message: string;
    type: ToastType;
  }>({
    show: false,
    message: '',
    type: ToastType.success,
  });

  const {
    payment,
    toFetchRecentPaymentsWithInvoices,
    isGatewayTransactionID,
    toResetPayment,
    resetPaymentAdjustment,
    toChargeCapture,
    toChargeCancel,
  } = usePayment();
  const { paymentConfig, getPaymentStatus } = usePaymentConfig();
  const { paymentStatus } = paymentConfig;
  const { organisation } = useOrganisation();
  const { settings, activeOrg } = organisation;
  const entityPermissions =
    paymentConfig?.msoPermissionsList?.entityContexts?.find(
      (entity) => entity.id === activeOrg?.treez_entity_id
    );

  const preprocessTransactionData = (
    data: PAYMENTS.ITransactionResult[]
  ): ITableData[] =>
    data.map(
      (row) =>
        ({
          ...row,
          customerName:
            !row.customer_first_name && !row.customer_last_name
              ? ''
              : row.customer_first_name === row.customer_last_name
              ? row.customer_first_name
              : `${row.customer_first_name || ''} ${
                  row.customer_last_name || ''
                }`.trim(),
          grossAmount: isGatewayTransactionID(row.id)
            ? CURRENCY.getGrossAmount(
                row?.gross_receipt_paid_calculated,
                row.gross_receipt_paid,
                row.order_amount
              )
            : row.charge_amount || row?.calculated_total,
          transactionDate: row.order_create_date || row?.create_date,
        } as ITableData)
    );

  const manuallyRecordSale = (row: any) => {
    setRecordSaleData(row);
    setRecordSale(true);
  };

  const isInvoiceRecord = (row: PAYMENTS.ITransactionResult): boolean =>
    row.gateway_invoice_id != null && row.gateway_invoice_id !== '';

  const canInvoiceBeActioned = (row: PAYMENTS.ITransactionResult): boolean => {
    const status = row.invoice_status?.toLowerCase();
    return (
      status === INVOICE_STATUS.NEW ||
      status === INVOICE_STATUS.SUBMITTED ||
      status === INVOICE_STATUS.CREATED
    );
  };

  const getFetchAPIFilter = (key?: string) => ({
    filter_by_user: true,
    key,
  });

  const showToastNotification = (
    message: string,
    type: ToastType = ToastType.success
  ) => {
    setToast({ show: true, message, type });
  };

  const refreshInvoices = () => {
    toFetchRecentPaymentsWithInvoices(getFetchAPIFilter());
  };

  const fetchTicketDetails = async (
    ticketAlphaNumId: string
  ): Promise<string | null> => {
    try {
      if (!ticketAlphaNumId) return null;
      if (ticketAlphaNumId.length === 7) {
        const uuid = crypto.randomUUID();
        console.log(
          `Generated UUID for 7-char ticket ${ticketAlphaNumId}: ${uuid}`
        );
        return uuid;
      }

      const baseUrl = activeOrg?.settings.sell_treez_url;
      const serviceUrl =
        baseUrl?.startsWith('http://') || baseUrl?.startsWith('https://')
          ? baseUrl
          : `https://${baseUrl}`;

      const response = await apiWithOrg.get<{
        ticketList: Array<{ ticket_id: string }>;
      }>(
        `${serviceUrl}/HintsService/v1.0/rest/ticket/external/ticket/ordernumber/${ticketAlphaNumId}`
      );

      if (response.data.ticketList && response.data.ticketList.length > 0) {
        return response.data.ticketList[0].ticket_id;
      }

      throw new Error(`No ticket found for ${ticketAlphaNumId}`);
    } catch (err) {
      console.error('Error fetching ticket details:', err);
      throw err;
    }
  };

  const handleInvoiceCapture = async (row: PAYMENTS.ITransactionResult) => {
    try {
      const ticketId = await fetchTicketDetails(
        row.track_id || row.reference_number || ''
      );
      const captureRequest = {
        invoiceId: row.gateway_invoice_id,
        processorId: row.processor_id,
        paymentChargeId: row.charge_id,
        ticketAlphaNumId: row.track_id || row.reference_number,
        ticketId,
        originalAmount: row.order_amount,
        feeAmount: row.order_fee_amount || 0,
        referenceNumber: row.reference_number,
        employeeReferenceId: row.associate_id,
        isSync: true,
      };
      const success = await toChargeCapture(captureRequest);
      if (success) {
        showToastNotification('Invoice captured successfully');
        refreshInvoices();
      } else {
        showToastNotification('Failed to capture invoice', ToastType.error);
      }
    } catch (err) {
      console.error('Capture invoice error:', err);
      showToastNotification(
        'An unexpected error occurred while capturing the invoice',
        ToastType.error
      );
    }
  };

  const handleInvoiceCancel = async (row: PAYMENTS.ITransactionResult) => {
    try {
      const ticketId = await fetchTicketDetails(
        row.track_id || row.reference_number || ''
      );
      const cancelRequest = {
        invoiceId: row.gateway_invoice_id,
        processorId: row.processor_id,
        paymentChargeId: row.charge_id,
        ticketAlphaNumId: row.track_id || row.reference_number,
        ticketId,
        originalAmount: row.order_amount,
        feeAmount: row.order_fee_amount || 0,
        referenceNumber: row.reference_number,
        employeeReferenceId: row.associate_id,
        isSync: true,
      };
      const success = await toChargeCancel(cancelRequest);
      if (success) {
        showToastNotification('Invoice cancelled successfully');
        refreshInvoices();
      } else {
        showToastNotification('Failed to cancel invoice', ToastType.error);
      }
    } catch (err) {
      console.error('Cancel invoice error:', err);
      showToastNotification(
        'An unexpected error occurred while cancelling the invoice',
        ToastType.error
      );
    }
  };

  const buildActionItems = (
    row: PAYMENTS.ITransactionResult
  ): ActionMenuItem[] => {
    const isInvoice = isInvoiceRecord(row);
    const canActionInvoice = isInvoice && canInvoiceBeActioned(row);
    const isPaymentSuccess = row.gateway_status === GTW_STATUS.SUCCESS;
    const hasRegularActions =
      !isInvoice && !(isPaymentSuccess || row.method === 'ACH');

    if (isInvoice && canActionInvoice) {
      const isCaptureDisabled =
        row.invoice_status?.toLowerCase() !== INVOICE_STATUS.SUBMITTED;
      return [
        {
          testId: 'capture-invoice',
          icon: 'check-circle',
          title: 'Capture',
          description:
            'Capture payment for this invoice and mark as completed.',
          disabled: isCaptureDisabled,
          onClick: () => handleInvoiceCapture(row),
        },
        {
          testId: 'cancel-invoice',
          icon: 'x-circle',
          title: 'Cancel',
          description: 'Cancel this invoice and void the payment request.',
          disabled: isCaptureDisabled,
          onClick: () => handleInvoiceCancel(row),
        },
      ];
    }

    if (!isInvoice && hasRegularActions) {
      const items: ActionMenuItem[] = [
        {
          testId: 'verify-sale',
          icon: 'refresh',
          title: 'Verify Sale',
          description: 'Check status of transaction with payment device.',
          onClick: () => {
            getPaymentStatus(row.gateway_id).then(() => {
              if (isActiveTab) setShowPaymentStatusModal(true);
            });
          },
        },
      ];
      if (
        entityPermissions?.permissions?.includes(
          'msotreezpay::createPaymentAdjustment'
        )
      ) {
        items.push({
          testId: 'update-status',
          icon: 'edit',
          title: 'Record Sale',
          description: 'Forcefully record this sale as successful.',
          onClick: () => manuallyRecordSale(row),
        });
      }
      return items;
    }

    return [];
  };

  const columns: ColumnConfig<ITableData>[] = [
    {
      key: 'transactionDate',
      label: 'Date',
      sortable: true,
      isDate: true,
      renderCell: (row) => (
        <>
          {row.transactionDate
            ? formatToTz(
                row.transactionDate,
                settings?.timezone,
                'MMM dd, hh:mm a'
              )
            : ''}
        </>
      ),
    },
    {
      key: 'grossAmount',
      label: 'Gross Amount',
      sortable: true,
      renderCell: (row) => (
        <div className="text-gray-blue-60 font-medium">
          {CURRENCY.convertToMainUnit(row.grossAmount)}
        </div>
      ),
    },
    {
      key: 'transaction_status',
      label: 'Status',
      renderCell: (row) => {
        const isInvoice = isInvoiceRecord(row);
        if (isInvoice) {
          return invoiceStatusFormatter(row?.invoice_status || '');
        }
        let status = row?.transaction_status || row?.order_status;
        if (isGatewayTransactionID(row.id)) {
          if (row?.adjusted_status !== null) {
            return (
              <span>
                {gatewatTransactionStatusFormatter(row?.adjusted_status)}
                <p className="text-xs text-gray-blue-60 leading-4">
                  Sale recorded as successful by {row.treez_employee_first_name}
                </p>
              </span>
            );
          }
          return gatewatTransactionStatusFormatter(row?.gateway_status);
        }
        if (
          status === 'inited' &&
          (String(row?.ach_debit_error_count) === '1' ||
            String(row?.ach_debit_error_count) === '2')
        ) {
          status = `${status} Retry ${String(row?.ach_debit_error_count)}`;
        }
        return transactionStatusFormatter(status);
      },
    },
    {
      key: 'method',
      label: 'Payment Type',
      renderCell: (row) => {
        if (!row.method) return PAYMENT_METHOD.ACH;
        if (row.method === PAYMENT_METHOD.ATM) return PAYMENT_METHOD.CATM;
        return row.method;
      },
    },
    {
      key: 'track_id',
      label: 'Ticket id',
      renderCell: (row) =>
        STRING.getTreezTicketIDFromReferenceNumber(row.reference_number) ||
        row.track_id,
    },
    {
      key: 'customerName',
      label: 'Customer',
      sortable: true,
      renderCell: (row) => (
        <div className="capitalize">{row.customerName || 'NA'}</div>
      ),
    },
    {
      key: 'transaction_action',
      label: 'Action',
      classNameCell:
        'sticky right-0 bg-white z-10 min-w-30 max-w-30 shadow-[-2px_0_4px_rgba(0,0,0,0.1)] flex items-center justify-center',
      renderCell: (row) => {
        const actions = buildActionItems(row);
        if (actions.length === 0) return null;

        return (
          <div onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 p-0"
                  aria-label="Open actions menu"
                >
                  <Icon
                    name="more"
                    size={20}
                    className="text-muted-foreground"
                    aria-hidden="true"
                  />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="p-2 min-w-70">
                {actions.map((item) => (
                  <ActionMenuItemRow key={item.testId} item={item} />
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  const handleRowClick = (
    row: PAYMENTS.ITransactionResult,
    _: SyntheticEvent
  ) => {
    if (isInvoiceRecord(row)) return;
    if (isGatewayTransactionID(row.id))
      navigate(`/${orgId}/payments/transactions/${row.gateway_id}`);
    else navigate(`/${orgId}/payments/transactions/${row.order_id}`);
  };

  const prevPage = () =>
    toFetchRecentPaymentsWithInvoices(getFetchAPIFilter(prevKey ?? undefined));
  const nextPage = () =>
    toFetchRecentPaymentsWithInvoices(getFetchAPIFilter(nextKey ?? undefined));

  useEffect(() => {
    if (isActiveTab) {
      toFetchRecentPaymentsWithInvoices(getFetchAPIFilter());
    }
  }, [isActiveTab]);

  useEffect(() => {
    return () => toResetPayment();
  }, []);

  useEffect(() => {
    if (!showPaymentStatusModal && paymentStatus === GTW_STATUS.APPROVED) {
      toFetchRecentPaymentsWithInvoices(getFetchAPIFilter());
    }
  }, [showPaymentStatusModal]);

  useEffect(() => {
    if (refreshTnx) {
      toFetchRecentPaymentsWithInvoices(getFetchAPIFilter());
      setRefreshTnx(false);
    }
  }, [refreshTnx]);

  useEffect(() => {
    if (payment.capturePaymentAdjustment === true) {
      setAdjustmentToast(true);
      resetPaymentAdjustment();
    }
  }, [payment.capturePaymentAdjustment]);

  useEffect(() => {
    if (payment.transactions?.items?.length > 0) {
      setNextKey(payment.transactions.next_key);
      setPrevKey(payment.transactions.prev_key);
      setNumResults(payment.transactions.total_count);
    } else {
      setNumResults(0);
    }
  }, [payment.transactions]);

  const customTheme: Theme = {
    Table: `
        grid-template-columns: minmax(120px, 200px) minmax(120px, 200px) minmax(120px, 200px) minmax(120px, 200px) minmax(120px, 200px) minmax(120px, 200px) 120px !important;
      `,
    BaseCell: `
      &:nth-of-type(7) {
        position: sticky !important;
        right: 0px !important;
        background-color: white !important;
        z-index: 2 !important;
        box-shadow: -2px 0 4px rgba(0, 0, 0, 0.1) !important;
        min-width: 120px !important;
        max-width: 120px !important;
      }
    `,
    HeaderCell: `
      &:nth-of-type(7) {
        position: sticky !important;
        right: 0px !important;
        background-color: white !important;
        z-index: 2 !important;
        box-shadow: -2px 0 4px rgba(0, 0, 0, 0.1) !important;
        font-weight: 600 !important;
        min-width: 120px !important;
        max-width: 120px !important;
      }
    `,
  };

  return (
    <>
      <div className="mb-3" />

      <div className="overflow-x-auto overflow-y-hidden relative w-full">
        <Table
          data={preprocessTransactionData(payment.transactions?.items || [])}
          columns={columns}
          onRowClick={handleRowClick}
          isLoading={Boolean(payment.isLoading)}
          isEmpty={!(payment?.transactions?.items?.length > 0)}
          defaultSortKey="transactionDate"
          customTheme={customTheme}
          noDataIndication={{
            heading: 'No transactions found',
            subText:
              'Note: You will only be able to view your own transactions',
          }}
        />
      </div>

      <Pagination
        pageId={location?.search}
        results={numResults}
        prevPage={prevPage}
        nextPage={nextPage}
        prevKey={prevKey}
        nextKey={nextKey}
      />

      {showPaymentStatusModal && (
        <PaymentStatus
          showModal={showPaymentStatusModal}
          setShowModal={setShowPaymentStatusModal}
          status={paymentStatus === GTW_STATUS.APPROVED}
        />
      )}

      {recordSale && recordSaleData && (
        <PaymentAdjustment
          refreshTnx={refreshTnx}
          setRefreshTnx={setRefreshTnx}
          showModal={recordSale}
          setShowModal={(arg) => setRecordSale(arg)}
          info={recordSaleData}
        />
      )}

      <ToastContainer
        showToast={adjustmentToast}
        onClose={() => setAdjustmentToast(false)}
        message="Sale recorded successfully. Please refresh after few seconds to see the updated sale."
        type={ToastType.success}
        showCross
      />

      <ToastContainer
        type={toast.type}
        showToast={toast.show}
        onClose={() => setToast({ ...toast, show: false })}
        message={toast.message}
        id="invoice-operation-toast"
        showCross
      />
    </>
  );
};

export default RecentPayments;
